import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { cn } from '../lib/utils';
import { QrCode } from 'lucide-react';

interface PinpadProps {
  onSubmit: (pin: string) => void;
  onSwitchToQR?: () => void;
  isLoading?: boolean;
  error?: string;
}

export const Pinpad: React.FC<PinpadProps> = ({ onSubmit, onSwitchToQR, isLoading, error }) => {
  const [pin, setPin] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleNumberClick = (number: string) => {
    if (pin.length < 4) {
      setPin(prev => prev + number);
      setHasSubmitted(false); // Reset submit flag when typing
    }
  };

  const handleClear = () => {
    setPin('');
    setHasSubmitted(false);
  };

  const handleSubmit = () => {
    if (pin.length === 4 && !hasSubmitted) {
      setHasSubmitted(true);
      onSubmit(pin);
    }
  };

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
    setHasSubmitted(false);
  };

  // Auto-submit when 4 digits are entered (only once)
  React.useEffect(() => {
    if (pin.length === 4 && !hasSubmitted) {
      const timer = setTimeout(() => {
        setHasSubmitted(true);
        onSubmit(pin);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [pin, onSubmit, hasSubmitted]);

  // Clear PIN when error occurs
  React.useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setPin('');
        setHasSubmitted(false);
      }, 2000); // Clear after 2 seconds to show error
      return () => clearTimeout(timer);
    }
  }, [error]);

  const pinDisplay = pin.replace(/./g, '●');

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <img 
            src="/logo.png" 
            alt="SZKŁO Sp. z o.o." 
            className="h-16 w-auto"
          />
        </div>
        <CardTitle className="text-2xl font-bold">Wprowadź PIN</CardTitle>
        <CardDescription>
          Wpisz swój 4-cyfrowy PIN aby się zalogować
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* PIN Display */}
        <div className="flex justify-center">
          <div className="flex space-x-2">
            {[0, 1, 2, 3].map((index) => (
              <div
                key={index}
                className={cn(
                  "w-12 h-12 rounded-lg border-2 flex items-center justify-center text-2xl font-bold",
                  index < pin.length 
                    ? "border-primary bg-primary/10 text-primary" 
                    : "border-gray-300 bg-gray-50"
                )}
              >
                {index < pin.length ? '●' : ''}
              </div>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-md">
            {error}
          </div>
        )}

        {/* Numpad */}
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((number) => (
            <Button
              key={number}
              variant="outline"
              size="xl"
              className="h-16 text-xl font-bold hover:bg-primary hover:text-primary-foreground"
              onClick={() => handleNumberClick(number.toString())}
              disabled={isLoading}
            >
              {number}
            </Button>
          ))}
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-3 gap-3">
          <Button
            variant="outline"
            size="xl"
            className="h-16 text-lg"
            onClick={handleClear}
            disabled={isLoading}
          >
            Wyczyść
          </Button>
          <Button
            variant="outline"
            size="xl"
            className="h-16 text-xl font-bold hover:bg-primary hover:text-primary-foreground"
            onClick={() => handleNumberClick('0')}
            disabled={isLoading}
          >
            0
          </Button>
          <Button
            variant="outline"
            size="xl"
            className="h-16 text-lg"
            onClick={handleBackspace}
            disabled={isLoading}
          >
            ⌫
          </Button>
        </div>

        {/* Submit Button */}
        <Button
          className="w-full h-12 text-lg font-semibold"
          onClick={handleSubmit}
          disabled={pin.length !== 4 || isLoading}
        >
          {isLoading ? 'Logowanie...' : 'Zaloguj'}
        </Button>

        {/* QR Scanner Toggle Button */}
        {onSwitchToQR && (
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-muted-foreground">Lub</span>
            </div>
          </div>
        )}

        {onSwitchToQR && (
          <Button
            variant="outline"
            className="w-full h-12 text-lg font-semibold"
            onClick={onSwitchToQR}
            disabled={isLoading}
          >
            <QrCode className="w-5 h-5 mr-2" />
            Skanuj kod QR
          </Button>
        )}
      </CardContent>
    </Card>
  );
};